import {$qs} from '../../../../@shared/ts/dom';
import {type CheckoutData} from '../../models/CheckoutData';
import {getLocaleText} from '../../util/translation';
import {PeachPayCustomer} from '../../reducers/peachPayCustomerReducer';
import chevronImageURL from '../../../../../public/img/chevron-down-solid.svg';

type FieldLocale = {
	label?: string;
	required?: boolean;
	hidden?: boolean;
	// The below exists but are not used by WC frontend JS. We also do not make
	// use them.
	placeholder?: string;
	priority?: number;
	class?: string[];
	label_class?: string[];
	autocomplete?: string;
	validate?: string[];
	type?: string;
};

/**
 * Sets up events to adjust the address form fields based on the selected country
 * and locale.
 */
function initFormLocaleEvents(checkoutData: CheckoutData, addressType: 'billing' | 'shipping') {
	const states = JSON.parse(checkoutData.country_state_options) as Record<string, Record<string, string>>;
	const locale = JSON.parse(checkoutData.country_field_locale) as Record<string, Record<string, FieldLocale>>;

	const $countryField = $qs<HTMLSelectElement>(`#pp-${addressType}-form [name="${addressType}_country"]`);
	if (!$countryField) {
		return;
	}

	const $form = $qs<HTMLFormElement>(`#pp-${addressType}-form`);
	if (!$form) {
		throw new Error(`Failed to locate the ${addressType} form element using the selector "#pp-${addressType}-form"`);
	}

	/**
	 * Handle rendering the field locale depending on the country value.
	 *
	 * Note: The country field may not exist if disabled so this will do nothing
     * in that case.
	 */
	adjustStateFieldToLocale(addressType, states[$countryField.value]);
	adjustAddressFieldsToLocale(addressType, locale[$countryField.value], locale['default']);

	$countryField.addEventListener('change', () => {
		const country = $countryField.value;

		const stateOptions = states[country];
		adjustStateFieldToLocale(addressType, stateOptions);

		adjustAddressFieldsToLocale(addressType, locale[country], locale['default']);
	});
}

/**
 * Renders the state field according to the selected country. This will change
 * the state input to be either hidden or visible. It will also convert the input
 * between a text/hidden input and a select input depending on the number of options.
 *
 * This is a copy of the WC logic located at: https://github.com/woocommerce/woocommerce/blob/d30c54ef846b086b96278375b71f7c379d9aa8e8/assets/js/frontend/country-select.js
 */
function adjustStateFieldToLocale(addressType: 'billing' | 'shipping', stateOptions: Record<string, string> | undefined) {
	const $stateContainer = $qs(`#${addressType}_state_field`);
	if (!$stateContainer) {
		throw new Error(`Failed to locate the ${addressType} state field container element using the selector "#${addressType}_state_field"`);
	}

	let $chevron = $stateContainer.querySelector<HTMLElement>('.pp-form-chevron');
	if (!$chevron) {
		$chevron = document.createElement('div');
		$chevron.classList.add('pp-form-chevron');
		$chevron.innerHTML = `<img src="${chevronImageURL}" />`;
		$stateContainer.append($chevron);
	}

	let $input = $stateContainer.querySelector<HTMLInputElement | HTMLSelectElement>('input,select');
	if (!$input) {
		throw new Error(`Failed to locate the ${addressType} state field element using the selector "#${addressType}_state_field input,select"`);
	}

	const inputId = $input.id;
	const inputName = $input.name;
	const inputValue = $input.value;

	if (stateOptions) {
		if (Object.keys(stateOptions).length === 0) {
			// Hide state field
			$stateContainer.style.display = 'none';
			$chevron.style.display = 'none';

			const $hiddenInput = document.createElement('input');
			$hiddenInput.type = 'hidden';
			$hiddenInput.name = inputName;
			$hiddenInput.id = inputId;
			$input.replaceWith($hiddenInput);
		} else {
			// Show state field as a select input
			$stateContainer.style.display = 'flex';
			$chevron.style.display = 'flex';

			let options = '';
			for (const [stateCode, stateName] of Object.entries(stateOptions)) {
				options += `<option value="${stateCode}" ${stateCode === inputValue ? 'selected' : ''}>${stateName}</option>`;
			}

			if ($input.nodeName !== 'SELECT') {
				const $selectInput = document.createElement('select');
				$selectInput.id = inputId;
				$selectInput.name = inputName;
				$selectInput.classList.add('state_select');
				$input.replaceWith($selectInput);
				$input = $selectInput;
			}

			$input.innerHTML = `<option value="">${getLocaleText('Select an option...')}</option>` + options;
			$input.dispatchEvent(new Event('change'));
		}
	} else {
		// Show the state field as a text input
		$stateContainer.style.display = 'flex';
		$chevron.style.display = 'none';

		if ($input.nodeName === 'SELECT' || ($input.nodeName === 'INPUT' && $input.type !== 'text')) {
			const $textInput = document.createElement('input');
			$textInput.id = inputId;
			$textInput.type = 'text';
			$textInput.name = inputName;
			$textInput.placeholder = ' ';
			$textInput.classList.add('text-input');
			$input.replaceWith($textInput);
		}
	}
}

/**
 * Adjusts the address fields order, labels, placeholders, etc. to the country
 * locale.
 *
 * This is a copy of the WC logic located at: https://github.com/woocommerce/woocommerce/blob/d30c54ef846b086b96278375b71f7c379d9aa8e8/assets/js/frontend/address-i18n.js
 */
function adjustAddressFieldsToLocale(addressType: 'billing' | 'shipping', countryLocale: Record<string, FieldLocale> | undefined, defaultLocale: Record<string, FieldLocale> | undefined) {
	const fields = [
		'address_1',
		'address_2',
		'state',
		'postcode',
		'city',
	];

	for (const fieldKey of fields) {
		const $fieldContainer = $qs(`#${addressType}_${fieldKey}_field`);
		if (!$fieldContainer) {
			continue;
		}

		if (countryLocale?.[fieldKey]?.hidden) {
			$fieldContainer.style.display = 'none';
			$fieldContainer.querySelector('input,select')?.setAttribute('disabled', 'disabled');
		} else {
			$fieldContainer.style.display = 'flex';
			$fieldContainer.querySelector('input,select')?.removeAttribute('disabled');
		}

		// Adjust label to locale
		const updateLabel = (label: string) => {
			const fieldLabel = $fieldContainer.querySelector('label');
			if (fieldLabel) {
				fieldLabel.innerHTML = label;
			}
		};

		if (countryLocale?.[fieldKey]?.label !== undefined) {
			updateLabel(countryLocale[fieldKey]!.label!);
		} else if (defaultLocale?.[fieldKey]?.label !== undefined) {
			updateLabel(defaultLocale[fieldKey]!.label!);
		}

		if (countryLocale?.[fieldKey]?.required ?? defaultLocale?.[fieldKey]?.required) {
			$fieldContainer.querySelector('label abbr')?.remove();
			$fieldContainer.querySelector('label')?.insertAdjacentHTML('beforeend', ` <abbr class="required" title="${getLocaleText('required')}">*</abbr>`);
			$fieldContainer.querySelector('input,select')?.setAttribute('required', 'required');
		} else {
			$fieldContainer.querySelector('label abbr')?.remove();
			$fieldContainer.querySelector('input,select')?.removeAttribute('required');
		}
	}
}

function renderLongAddress(): void {
	const $longAddress = $qs<HTMLSpanElement>('#long-address');

	if (!$longAddress) {
		throw new Error('Failed to locate the long address element using the selector "#long-address"');
	}

	if (PeachPayCustomer.shipToDifferentAddress()) {
		$longAddress.innerText = PeachPayCustomer.shipping.formattedAddress().join('\n');
	} else {
		$longAddress.innerText = PeachPayCustomer.billing.formattedAddress().join('\n');
	}
}

export {
	initFormLocaleEvents,
	renderLongAddress,
};
